import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api/client'
import { asList } from '@/lib/api/list'

/**
 * Ödeme onayı: doğrudan ödeme yetkisi olmayanın çıkan ödemesi talep olarak açılır; firma sahibi ve ortaklar (onay yetkisi
 * olanlar) onaylayınca işlenir. Eşik altı tek onay, eşik ve üstü tüm onaycılar; talep eden onaycıysa onayı sayılır.
 */
export type PaymentRequestStatus = 'pending' | 'approved' | 'rejected' | 'cancelled' | 'failed'
export type PaymentRequestKind = 'cash_out' | 'ledger_payment' | 'obligation_pay'

export type PaymentRequest = {
  id: string
  kind: PaymentRequestKind
  status: PaymentRequestStatus
  level: 'single' | 'all'
  amount: string
  summary: string
  reason: string
  requestedBy: string
  requestedByName: string
  approvers: { userId: string; name: string; approved: boolean }[]
  decidedByName: string
  decidedAt: string
  decisionNote: string
  createdAt: string
  canApprove: boolean
  canReject: boolean
  canCancel: boolean
}

type Row = Record<string, unknown>
const s = (v: unknown) => (v == null ? '' : String(v))
const keys = {
  list: (status: string) => ['finance', 'payment-requests', status] as const,
  settings: ['finance', 'payment-requests', 'settings'] as const,
}

const map = (r: Row): PaymentRequest => ({
  id: s(r.id),
  kind: s(r.kind) as PaymentRequestKind,
  status: (s(r.status) || 'pending') as PaymentRequestStatus,
  level: r.approval_level === 'all' ? 'all' : 'single',
  amount: s(r.amount),
  summary: s(r.summary),
  reason: s(r.reason),
  requestedBy: s(r.requested_by),
  requestedByName: s(r.requested_by_name),
  approvers: (Array.isArray(r.approvers) ? (r.approvers as Row[]) : []).map((a) => ({
    userId: s(a.user_id),
    name: s(a.name),
    approved: a.approved === true,
  })),
  decidedByName: s(r.decided_by_name),
  decidedAt: s(r.decided_at),
  decisionNote: s(r.decision_note),
  createdAt: s(r.created_at),
  canApprove: r.can_approve === true,
  canReject: r.can_reject === true,
  canCancel: r.can_cancel === true,
})

export const usePaymentRequests = (status: string) =>
  useQuery({
    queryKey: keys.list(status),
    queryFn: async () => asList(await api.get<Row[]>('/api/v1/finance/payment-requests', { status })).map(map),
  })

export const usePaymentApprovalSettings = () =>
  useQuery({
    queryKey: keys.settings,
    queryFn: async () => {
      const r = await api.get<Row>('/api/v1/finance/payment-requests/settings')
      return { threshold: s(r.threshold), approvers: Number(r.approvers) || 0 }
    },
  })

/** Onay ödemeyi işleyebilir (hesap bakiyeleri değişir): tüm finans sorguları tazelenir. */
function useReqMut<A>(fn: (a: A) => Promise<unknown>) {
  const qc = useQueryClient()
  return useMutation({ mutationFn: fn, onSettled: () => qc.invalidateQueries({ queryKey: ['finance'] }) })
}

export const useApprovePayment = () =>
  useReqMut((id: string) => api.post(`/api/v1/finance/payment-requests/${id}/approve`, {}))
export const useRejectPayment = () =>
  useReqMut(({ id, note }: { id: string; note: string }) =>
    api.post(`/api/v1/finance/payment-requests/${id}/reject`, { note }),
  )
export const useCancelPayment = () =>
  useReqMut((id: string) => api.post(`/api/v1/finance/payment-requests/${id}/cancel`, {}))
export const useSavePaymentThreshold = () =>
  useReqMut((threshold: string) => api.put('/api/v1/finance/payment-requests/settings', { threshold }))
